/**
 * PROJECT-LAUNCH-REVIEW.MODAL
 * Review of the project before the survey is created, fullscreen : the steps as a path on the left,
 * the detail of the selected step on the right, the owl and the progress on top
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import Modal from "@/components/modal"
import ToggleButton from "@/components/toggle-button"
import AiToggle from "@/components/ai-toggle"
import { WithTranslation, withTranslation } from "react-i18next"
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, IconDefinition } from "@fortawesome/free-solid-svg-icons"
import Tooltip from "@/components/tooltip"
import ReviewMessage, { ReviewMessageGroup, tint } from "@/widgets/review-message.widget"
import { useAnimationsDisabled } from "@/utils/animation-mode.utils"

export interface LaunchReviewRow {
  id: string //Step
  icon: IconDefinition
  errors: string[] //Messages (translation keys)
  warnings: string[] //Messages (translation keys)
  isUnconfirmed: boolean
  errorsCount?: number //Lines of errors counted as the card of the step does (the questionnaire has one line per problem), else the number of messages
  warningsCount?: number
  languages?: { [message: string]: string[] } //Languages in which the text of an alert is missing (key : text of the message)
  ai?: string[] //Messages that come from the AI (a small logo is shown)
}

export const countRowErrors = (row: LaunchReviewRow): number => (row.errors.length > 0 ? (row.errorsCount ?? row.errors.length) : 0)
export const countRowWarnings = (row: LaunchReviewRow): number => (row.warnings.length > 0 ? (row.warningsCount ?? row.warnings.length) : 0)

interface OwnProps extends WithTranslation {
  rows: LaunchReviewRow[]
  onClose: Function
  onGoTo: (stepId: string) => void //Open the step to fix
  onGoToMessage: (stepId: string, message: string, language?: string) => void //Go to the place where the problem of a message is fixed (in a language, for a text missing in several)
  onLaunch: Function
  initialStepId?: string //Step open when the owl was called : the detail starts there
  isGuideEnabled: boolean //The owl comes back after each step confirmed
  onGuideChange: (isEnabled: boolean) => void
  isAiAvailable?: boolean //The account has the AI module of the project assistant
  isAiEnabled?: boolean //The owl proposes adjustments from the AI
  onAiChange?: (isEnabled: boolean) => void
}

type Severity = "error" | "unconfirmed" | "warning" | "ok"

const COLORS: Record<Severity, string> = {
  error: "var(--idt-red)",
  unconfirmed: "var(--idt-grey)",
  warning: "var(--idt-orange)",
  ok: "var(--idt-green)"
}

//Light background of the same colour

const RING_RADIUS: number = 38
const RING_LENGTH: number = 2 * Math.PI * RING_RADIUS
const RING_CAP: number = 4 //Half of the stroke : the rounded end of an arc
const RING_GAP: number = 8 //Space between the arcs (the caps included)
const SEVERITY_ORDER: Severity[] = ["ok", "warning", "unconfirmed", "error"] //The ring starts with what is ready

export function getSeverity(row: LaunchReviewRow): Severity {
  if (row.errors.length > 0) return "error"
  if (row.isUnconfirmed) return "unconfirmed"
  if (row.warnings.length > 0) return "warning"
  return "ok"
}

function ProjectLaunchReviewModal(props: OwnProps) {
  const { t } = props
  const isAnimationsDisabled = useAnimationsDisabled()

  const rowsToSee: LaunchReviewRow[] = props.rows.filter((row) => getSeverity(row) !== "ok")
  const readyCount: number = props.rows.length - rowsToSee.length

  //Opens on the step the user is in, else on the first error, else on the first point to look at (never on a step that is ready)
  const [selectedId, setSelectedId] = useState<string>((props.rows.find((row) => row.id === props.initialStepId) || rowsToSee.find((row) => getSeverity(row) === "error") || rowsToSee[0] || props.rows[0])?.id)
  const selected: LaunchReviewRow | undefined = props.rows.find((row) => row.id === selectedId) || props.rows[0]

  //One arc per colour (ready, warnings, to confirm, errors), as long as the number of steps it holds
  const ringGroups = SEVERITY_ORDER.map((severity) => ({ severity, count: props.rows.filter((row) => getSeverity(row) === severity).length })).filter((group) => group.count > 0)
  const ringRoom: number = RING_LENGTH - ringGroups.length * RING_GAP
  let ringCursor: number = 0
  const ringArcs = ringGroups.map((group) => {
    const share: number = (group.count / props.rows.length) * ringRoom
    //The rounded ends add half of the stroke on each side : the arc itself is shorter
    const arc = { severity: group.severity, start: ringCursor + RING_CAP, length: Math.max(share - 2 * RING_CAP, 0.01) }
    ringCursor += share + RING_GAP
    return arc
  })

  const errorsCount: number = props.rows.reduce((sum, row) => sum + countRowErrors(row), 0)
  const unconfirmedCount: number = props.rows.filter((row) => row.isUnconfirmed).length
  const warningsCount: number = props.rows.reduce((sum, row) => sum + countRowWarnings(row), 0)
  const isBlocked: boolean = errorsCount > 0 || unconfirmedCount > 0

  //One line under the sentence : the counts that are not zero
  const summary: string = [
    errorsCount > 0 && t("project_review_errors", { count: errorsCount, s: errorsCount > 1 ? "s" : "" }),
    unconfirmedCount > 0 && t("project_review_unconfirmed", { count: unconfirmedCount, s: unconfirmedCount > 1 ? "s" : "" }),
    warningsCount > 0 && t("project_review_warnings", { count: warningsCount, s: warningsCount > 1 ? "s" : "" })
  ]
    .filter(Boolean)
    .join(" · ")

  //The detail of every step is under the other, and the path follows the scroll
  const scrollRef = useRef<HTMLDivElement>(null)
  const isJumpingRef = useRef<boolean>(false) //A click on the path scrolls : the scroll is not read as a choice during that time

  //Click on a step of the path : the detail comes to the top
  function jumpTo(id: string) {
    setSelectedId(id)
    const section = document.getElementById("review-section-" + id)
    if (!section || !scrollRef.current) return
    isJumpingRef.current = true
    scrollRef.current.scrollTo({ top: section.offsetTop, behavior: isAnimationsDisabled ? "auto" : "smooth" })
    setTimeout(() => (isJumpingRef.current = false), 700)
  }

  //The step selected on the path is the last one whose top has passed the top of the detail
  function followScroll() {
    const container = scrollRef.current
    if (!container || isJumpingRef.current) return
    let current: string = props.rows[0]?.id
    props.rows.forEach((row) => {
      const section = document.getElementById("review-section-" + row.id)
      if (section && section.offsetTop <= container.scrollTop + 40) current = row.id
    })
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 4) current = props.rows[props.rows.length - 1]?.id
    if (current && current !== selectedId) setSelectedId(current)
  }

  //Opening : the detail starts on the first step to look at
  useLayoutEffect(() => {
    const open = () => {
      const section = document.getElementById("review-section-" + selectedId)
      if (section && scrollRef.current) scrollRef.current.scrollTop = section.offsetTop
    }
    open()
    //Once more after the layout of the modal is settled
    const frame = requestAnimationFrame(open)
    return () => cancelAnimationFrame(frame)
  }, [])

  //The path keeps the selected step in view
  useEffect(() => {
    document.getElementById("review-node-" + selectedId)?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [selectedId])

  //Messages are "<where> : <what is wrong>" : the same problem found on several places is said once, with a link to each place
  //(a message that starts with a count, "18 collaborateurs sans … : …", is already the sum of its places)
  const PLURAL_KINDS: string[] = ["question_empty", "axis_unnamed", "axis_empty"]
  const groupTextByProblem: { [problem: string]: string } = {}
  PLURAL_KINDS.forEach((kind) => (groupTextByProblem[t("template_issue_" + kind)] = t("template_issue_group_" + kind)))
  const splitMessage = (text: string): { where: string; problem: string } => {
    const [where, ...rest] = t(text).split(" : ")
    return { where: where.replace(/\*\*/g, ""), problem: rest.join(" : ") }
  }
  const isGroupable = (text: string): boolean => {
    const { where, problem } = splitMessage(text)
    return !!problem && !/^\d/.test(where)
  }

  function renderMessages(row: LaunchReviewRow) {
    const messages: { text: string; kind: "error" | "warning" }[] = [
      ...row.errors.map((text) => ({ text, kind: "error" as const })),
      ...row.warnings.map((text) => ({ text, kind: "warning" as const }))
    ]
    const sameProblem = (message: { text: string; kind: string }) =>
      messages.filter((x) => x.kind === message.kind && isGroupable(x.text) && splitMessage(x.text).problem === splitMessage(message.text).problem)
    const done: { [key: string]: boolean } = {}
    return messages.map((message, i) => {
      const group = isGroupable(message.text) ? sameProblem(message) : []
      if (group.length > 1) {
        const { problem } = splitMessage(message.text)
        const key: string = message.kind + problem
        if (done[key]) return null
        done[key] = true
        return (
          <ReviewMessageGroup
            key={row.id + key}
            kind={message.kind}
            text={groupTextByProblem[problem] || problem}
            items={group.map((x) => ({ label: splitMessage(x.text).where, onClick: () => props.onGoToMessage(row.id, x.text) }))}
          />
        )
      }
      return (
        <ReviewMessage
          key={row.id + i}
          index={i}
          kind={message.kind}
          text={t(message.text)}
          languages={row.languages?.[t(message.text)] ?? row.languages?.[message.text]}
          isAi={!!(row.ai?.includes(t(message.text)) || row.ai?.includes(message.text))}
          onLanguage={(language) => props.onGoToMessage(row.id, message.text, language)}
          onClick={() => props.onGoToMessage(row.id, message.text)}
        />
      )
    })
  }

  return (
    <Modal
      isFullScreen
      className="launch-review-modal"
      onClose={props.onClose}
      isCloseButtonVisible
      footerLeft={
        //Centered on the height of the buttons of the footer
        <div className="flex" style={{ alignSelf: "center", alignItems: "center" }}>
          <Tooltip
            text={
              t(props.isGuideEnabled ? "project_review_guide_help_on" : "project_review_guide_help_off") +
              (props.isAiAvailable ? "" : " " + t("project_review_no_ai_note"))
            }
            position="top"
            usePortal>
            <div className="flex" style={{ alignItems: "center", gap: 8 }}>
              <ToggleButton isActive={props.isGuideEnabled} onToogle={() => props.onGuideChange(!props.isGuideEnabled)} />
              <span className="grey-t text-sm">{t("project_review_guide")}</span>
            </div>
          </Tooltip>
          {props.isAiAvailable && (
            <Tooltip
              text={t(props.isAiEnabled ? "project_review_ai_help_on" : "project_review_ai_help_off")}
              position="top"
              usePortal>
              <div style={{ marginLeft: 24 }}>
                <AiToggle
                  isActive={!!props.isAiEnabled}
                  label={t("project_review_ai")}
                  onToggle={() => props.onAiChange?.(!props.isAiEnabled)}
                />
              </div>
            </Tooltip>
          )}
        </div>
      }
      onNext={props.onLaunch}
      onNextCta={t(isBlocked ? "project_launch" : "project_review_launch_anyway")}
      nextDisabled={isBlocked}
      nextDisabledTooltip={isBlocked ? t("project_review_blocked") : undefined}>
      <div className="flex" style={{ flexDirection: "column", height: "100%", gap: 14 }}>
        {/* The owl says it, and the ring counts the steps that are ready */}
        <div className="flex" style={{ alignItems: "center", gap: 20 }}>
          <img src={require("@/assets/owl.png")} alt="" style={{ width: 60, height: "auto" }} />

          <div className="flex1" style={{ padding: "10px 18px", borderRadius: 16, background: "var(--idt-lightwhite)" }}>
            <div className="dark-t" style={{ fontSize: 18, fontWeight: 700 }}>
              {t(isBlocked ? "project_review_headline_blocked" : "project_review_headline_warnings")}
            </div>
            <div className="grey-t text-sm">{summary}</div>
          </div>

          <div style={{ position: "relative", width: 92, height: 92, flexShrink: 0 }}>
            <svg width="92" height="92" viewBox="0 0 92 92" style={{ transform: "rotate(-90deg)" }}>
              <circle cx="46" cy="46" r={RING_RADIUS} fill="none" stroke="var(--idt-lightwhite)" strokeWidth="8" />
              {ringArcs.map((arc, i) => (
                <motion.circle
                  key={arc.severity}
                  cx="46"
                  cy="46"
                  r={RING_RADIUS}
                  fill="none"
                  stroke={COLORS[arc.severity]}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDashoffset={-arc.start}
                  initial={{ strokeDasharray: isAnimationsDisabled ? `${arc.length} ${RING_LENGTH}` : `0.01 ${RING_LENGTH}` }}
                  animate={{ strokeDasharray: `${arc.length} ${RING_LENGTH}` }}
                  transition={{ duration: 0.6, delay: isAnimationsDisabled ? 0 : i * 0.12, ease: "easeOut" }}
                />
              ))}
            </svg>
            <div
              className="flex dark-t"
              style={{ position: "absolute", inset: 0, alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
              <b style={{ fontSize: 18, lineHeight: 1 }}>
                {readyCount}/{props.rows.length}
              </b>
              <span className="grey-t text-sm" style={{ lineHeight: 1, marginTop: 2 }}>
                {t("project_review_ready_short")}
              </span>
            </div>
          </div>
        </div>

        <div className="flex" style={{ gap: 28, flex: 1, minHeight: 0 }}>
          {/* The path : one node per step */}
          <div style={{ width: 420, flexShrink: 0, overflowY: "auto", padding: "12px 6px 12px 18px" }}>
            {props.rows.map((row, index) => {
              const severity: Severity = getSeverity(row)
              const color: string = COLORS[severity]
              const isSelected: boolean = row.id === selected?.id
              return (
                <div key={row.id} id={"review-node-" + row.id} className="flex" style={{ gap: 12, alignItems: "stretch" }}>
                  <div className="flex" style={{ flexDirection: "column", alignItems: "center", width: 34, flexShrink: 0 }}>
                    <motion.div
                      initial={isAnimationsDisabled ? false : { scale: 0 }}
                      animate={{ scale: isSelected ? 1.1 : 1 }}
                      transition={{ type: "spring", stiffness: 380, damping: 20, delay: isAnimationsDisabled ? 0 : index * 0.04 }}
                      className="flex"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        color: "white",
                        background: color,
                        boxShadow: isSelected ? `0 0 0 4px ${tint(color)}, 0 0 0 6px ${color}` : undefined
                      }}>
                      <FontAwesomeIcon icon={severity === "ok" ? faCheck : row.icon} />
                    </motion.div>
                    {index < props.rows.length - 1 && (
                      <div style={{ width: 3, flex: 1, minHeight: 8, borderRadius: 2, background: "var(--idt-medgrey)" }} />
                    )}
                  </div>

                  <motion.div
                    whileHover={isAnimationsDisabled ? undefined : { x: 3 }}
                    className="flex1 flex cursor-pointer"
                    style={{
                      alignItems: "center",
                      gap: 8,
                      minHeight: 34,
                      padding: "2px 12px",
                      borderRadius: 10,
                      marginBottom: index < props.rows.length - 1 ? 8 : 0,
                      background: isSelected ? tint(color) : undefined
                    }}
                    onClick={() => jumpTo(row.id)}>
                    <span className={severity === "ok" ? "grey-t" : "dark-t"} style={{ fontWeight: isSelected ? 700 : 400, whiteSpace: "nowrap" }}>
                      {t("project_setup_" + row.id)}
                    </span>
                    <div className="flex1" />
                    {/* The errors, then the warnings next to them (smaller pills : the label has room) */}
                    {[
                      { value: countRowErrors(row), color: COLORS.error },
                      { value: countRowWarnings(row), color: COLORS.warning }
                    ]
                      .filter((pill) => pill.value > 0)
                      .map((pill) => (
                        <span
                          key={pill.color}
                          className="text-sm"
                          style={{ minWidth: 20, padding: "0 6px", textAlign: "center", borderRadius: 999, color: "white", background: pill.color }}>
                          {pill.value}
                        </span>
                      ))}
                  </motion.div>
                </div>
              )
            })}
          </div>

          {/* The detail of every step : the path on the left follows the scroll */}
          <div ref={scrollRef} className="flex1" onScroll={followScroll} style={{ overflowY: "auto", minWidth: 0, paddingRight: 6, position: "relative" }}>
            {props.rows.map((row) => {
              const severity: Severity = getSeverity(row)
              return (
                <div key={row.id} id={"review-section-" + row.id} style={{ marginBottom: 36 }}>
                  <div
                    className="flex cursor-pointer"
                    style={{ alignItems: "center", gap: 16, marginBottom: 14 }}
                    onClick={() => props.onGoTo(row.id)}>
                    <div
                      className="flex"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 20,
                        color: "white",
                        background: COLORS[severity]
                      }}>
                      <FontAwesomeIcon icon={row.icon} />
                    </div>
                    <div className="flex1">
                      <div className="dark-t" style={{ fontSize: 18, fontWeight: 700 }}>
                        {t("project_setup_" + row.id)}
                      </div>
                      {severity === "unconfirmed" && <div className="grey-t text-sm">{t("project_review_to_confirm")}</div>}
                    </div>
                  </div>

                  {severity === "ok" ? (
                    <div
                      className="flex"
                      style={{
                        alignItems: "center",
                        gap: 12,
                        padding: "14px 18px",
                        borderRadius: 12,
                        background: tint(COLORS.ok),
                        color: "var(--idt-darkgreen)"
                      }}>
                      <FontAwesomeIcon icon={faCheck} />
                      {t("project_review_step_ready")}
                    </div>
                  ) : (
                    renderMessages(row)
                  )}
                </div>
              )
            })}
            <div style={{ height: "40vh" }} />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default withTranslation()(ProjectLaunchReviewModal)
